// Time-grid for the Week / Day calendar views. Bookings are positioned against
// an hourly axis; empty space is click-to-book; blocks can be dragged to a new
// day/time (snapped to 15 min) which calls onReschedule.

import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { to12h, type ApptCalendarBooking } from '@/services/appointments.service';
import BookingHoverCard from './BookingHoverCard';

const HOUR_H = 64;          // px per hour
const SNAP = 15;            // minutes
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
const toHHMM = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

const STATUS_BG: Record<string, string> = {
  PENDING: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  CONFIRMED: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40',
  COMPLETED: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  CANCELLED: 'bg-red-500/12 text-red-700 dark:text-red-300 border-red-500/40',
  NO_SHOW: 'bg-slate-500/12 text-slate-700 dark:text-slate-300 border-slate-500/40',
};

interface Props {
  days: Date[];
  bookingsByDay: Map<string, ApptCalendarBooking[]>;
  onOpen: (bookingUid: string) => void;
  onNewAt: (date: string, start: string) => void;
  onReschedule: (b: ApptCalendarBooking, date: string, start: string) => void;
}

// lane layout so overlapping bookings sit side-by-side
const layout = (list: ApptCalendarBooking[]) => {
  const sorted = [...list].sort((a, b) => toMin(a.start) - toMin(b.start));
  const lanes: number[] = []; // lane -> end minute
  const placed = sorted.map((b) => {
    const s = toMin(b.start), e = toMin(b.end);
    let lane = lanes.findIndex((end) => end <= s);
    if (lane === -1) { lane = lanes.length; lanes.push(e); } else lanes[lane] = e;
    return { b, s, e, lane };
  });
  const laneCount = Math.max(1, lanes.length);
  return { placed, laneCount };
};

const AppointmentTimeGrid = ({ days, bookingsByDay, onOpen, onNewAt, onReschedule }: Props) => {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<{ b: ApptCalendarBooking; dx: number; dy: number; day: string; start: string } | null>(null);

  // visible hour window: fit bookings, default 8..20
  const [startHour, endHour] = useMemo(() => {
    let min = 8 * 60, max = 20 * 60;
    bookingsByDay.forEach((list) => list.forEach((b) => { min = Math.min(min, toMin(b.start)); max = Math.max(max, toMin(b.end)); }));
    return [Math.max(0, Math.floor(min / 60)), Math.min(24, Math.ceil(max / 60))];
  }, [bookingsByDay]);
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const gridH = (endHour - startHour) * HOUR_H;

  // pointer drag
  const onPointerDown = (e: React.PointerEvent, b: ApptCalendarBooking) => {
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setDrag({ b, dx: 0, dy: 0, day: b.date, start: b.start });
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag || !gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const gutter = 56;
    const colW = (rect.width - gutter) / days.length;
    const x = e.clientX - rect.left - gutter;
    const y = e.clientY - rect.top;
    let dayIdx = Math.floor(x / colW);
    dayIdx = Math.max(0, Math.min(days.length - 1, dayIdx));
    let mins = startHour * 60 + Math.round((y / HOUR_H) * 60 / SNAP) * SNAP;
    const dur = toMin(drag.b.end) - toMin(drag.b.start);
    mins = Math.max(startHour * 60, Math.min(endHour * 60 - dur, mins));
    setDrag({ ...drag, day: ymd(days[dayIdx]), start: toHHMM(mins), dx: 1, dy: 1 });
  };
  const onPointerUp = () => {
    if (!drag) return;
    const moved = drag.day !== drag.b.date || drag.start !== drag.b.start;
    if (moved) onReschedule(drag.b, drag.day, drag.start);
    setDrag(null);
  };

  return (
    <div
      ref={gridRef}
      className="relative border border-border rounded-xl overflow-hidden bg-surface select-none"
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* day headers */}
      <div className="flex border-b border-border bg-surface-alt/40 sticky top-0 z-10">
        <div className="w-14 shrink-0" />
        {days.map((d) => {
          const today = ymd(d) === ymd(new Date());
          return (
            <div key={ymd(d)} className="flex-1 text-center py-1.5 border-l border-border">
              <div className="text-[10.5px] uppercase text-text-muted">{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
              <div className={clsx('text-[14px] font-bold', today ? 'text-primary' : 'text-text-primary')}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>

      {/* body */}
      <div className="flex" style={{ height: gridH }}>
        {/* hour gutter */}
        <div className="w-14 shrink-0 relative">
          {hours.map((h) => (
            <div key={h} style={{ height: HOUR_H }} className="relative">
              <span className="absolute -top-2 right-1.5 text-[10px] text-text-muted">{to12h(toHHMM(h * 60))}</span>
            </div>
          ))}
        </div>

        {/* day columns */}
        {days.map((d) => {
          const key = ymd(d);
          const { placed, laneCount } = layout(bookingsByDay.get(key) ?? []);
          return (
            <div
              key={key}
              className="flex-1 relative border-l border-border"
              onClick={(e) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                const y = e.clientY - rect.top;
                const mins = startHour * 60 + Math.floor((y / HOUR_H) * 60 / SNAP) * SNAP;
                onNewAt(key, toHHMM(Math.max(startHour * 60, mins)));
              }}
            >
              {/* hour lines */}
              {hours.map((h) => <div key={h} style={{ height: HOUR_H }} className="border-b border-border/50" />)}

              {/* booking blocks */}
              {placed.map(({ b, s, e, lane }) => {
                const isDragging = drag?.b.bookingUid === b.bookingUid;
                const top = ((isDragging ? toMin(drag!.start) : s) - startHour * 60) / 60 * HOUR_H;
                const rawHeight = (e - s) / 60 * HOUR_H;
                const height = Math.max(24, rawHeight);
                // show only as many lines as fit, so text never gets clipped mid-letter
                const lines = height >= 58 ? 3 : height >= 40 ? 2 : 1;
                const wPct = 100 / laneCount;
                if (isDragging && drag!.day !== key) return null;
                return (
                  <BookingHoverCard key={b.bookingUid} booking={b}>
                    <div
                      onPointerDown={(ev) => onPointerDown(ev, b)}
                      onClick={(ev) => { ev.stopPropagation(); if (!isDragging) onOpen(b.bookingUid); }}
                      style={{
                        position: 'absolute', top, height,
                        left: `calc(${lane * wPct}% + 2px)`, width: `calc(${wPct}% - 4px)`,
                        zIndex: isDragging ? 30 : 1,
                      }}
                      className={clsx(
                        'rounded-md border px-1.5 text-[10.5px] cursor-grab active:cursor-grabbing overflow-hidden transition-shadow flex flex-col justify-center',
                        STATUS_BG[b.status] ?? 'bg-surface-alt',
                        b.status === 'CANCELLED' && 'line-through opacity-70',
                        isDragging && 'shadow-elevated ring-2 ring-primary',
                      )}
                    >
                      {lines === 1 ? (
                        <div className="font-semibold truncate leading-none">{to12h(b.start)} · {b.customerName || b.serviceName}</div>
                      ) : (
                        <>
                          <div className="font-bold truncate leading-[1.15]">{to12h(b.start)}</div>
                          <div className="font-semibold truncate leading-[1.15]">{b.serviceName}</div>
                          {lines >= 3 && <div className="truncate opacity-80 leading-[1.15]">{b.customerName || b.customerPhone || b.bookingNo}</div>}
                        </>
                      )}
                    </div>
                  </BookingHoverCard>
                );
              })}
            </div>
          );
        })}
      </div>

      {drag && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-40 bg-primary text-on-primary text-[11px] font-semibold rounded-full px-3 py-1 shadow-elevated pointer-events-none">
          {new Date(`${drag.day}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} · {to12h(drag.start)}
        </div>
      )}
    </div>
  );
};

export default AppointmentTimeGrid;
